import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { toast } from 'react-toastify';
import { Availability } from '../../entities/Availability/index.js';
import { hasChangesAvailability } from '../../shared/utils/compareAvailability.js';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { LanguageOptions } from '../../entities/LanguageOptions/index.js';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { Form, Row, Col } from 'react-bootstrap';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

const EditNewsPage = () => {
  const [mainImg, setMainImage] = useState();
  const [oldData, setOldData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const params = useParams();
  const navigate = useNavigate();

  const handleRequest = async () => {
    const body = new FormData();
    if (mainImg) {
      body.append('mainImg', mainImg);
    }
    // Убедимся, что ID новости передается в запросе
    const formData = { ...form, id: params.id };
    body.append('model', JSON.stringify(formData));

    // Обновление доступности новостей
    if (hasChangesAvailability(oldData.availability, form.availability)) {
      try {
        await apiRequests.news.updateAvailability(form.availability);
        console.log('Доступность новостей успешно обновлена');
      } catch (error) {
        // Получаем текст ошибки из ответа сервера
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Произошла ошибка при обновлении доступности';
        
        // Показываем уведомление об ошибке
        toast.error(errorMessage);
        console.error('Ошибка при обновлении доступности:', error.response?.data || error);
        return; // Прерываем выполнение функции в случае ошибки
      }
    }

    // Используем стандартный подход с then/catch вместо toast.promise для лучшей обработки ошибок
    const toastId = toast.loading('Запрос к серверу...');
    
    apiRequests.news.update(body)
      .then(response => {
        toast.update(toastId, { 
          render: 'Данные успешно изменены', 
          type: 'success', 
          isLoading: false,
          autoClose: 3000
        });
        console.log('Новость успешно обновлена:', response);
      })
      .catch(error => {
        // Получаем текст ошибки из ответа сервера
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Произошла ошибка при обновлении новости';
        
        toast.update(toastId, { 
          render: errorMessage, 
          type: 'error', 
          isLoading: false,
          autoClose: 5000
        });
        
        console.error('Ошибка при обновлении новости:', error.response?.data || error);
      });
  };

  useEffect(() => {
    const getData = () => {
      apiRequests.news.getById(params.id).then((res) => {
        // Если SEOMetatags не существует в ответе API, добавляем его с пустыми значениями
        if (!res.SEOMetatags) {
          res.SEOMetatags = {
            Title: null,
            Description: null,
            Keywords: null,
          };
        }
        setForm(res);
        setOldData(res);
      });
    };
    getData();
  }, []);

  if (!form) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleRequest}
      onBack={() => navigate('/news')}
      saving={saving}
      submitText="Сохранить"
      savingText="Сохранение..."
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Заголовок" required>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Новости компании"
              />
            </FormField>

            <FormField label="Текст">
              <Form.Control
                as="textarea"
                rows={3}
                value={form.shortDescription}
                onChange={(e) => setForm((prev) => ({ ...prev, shortDescription: e.target.value }))}
                placeholder="Введите текст новости"
              />
            </FormField>
          </Col>

          <Col lg={4}>
            <FormField label="Изображение">
              <UploadPicture onSelect={setMainImage} initialValue={form.imgPath} entityType="news" />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Настройки">
        <Row>
          <Col lg={6}>
            <LanguageOptions
              initialValue={form.mainLanguageId}
              onSelect={(val) => setForm((prev) => ({ ...prev, mainLanguageId: val }))}
            />
          </Col>
        </Row>

        <div className="mt-4">
          <Form.Label className="fw-medium mb-3">Доступность по странам</Form.Label>
          <Availability
            initialValues={form.availability}
            onSelect={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, availableInAllCountries: val },
              }))
            }
            onSelectAllowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, allowedCountriesIds: val },
              }))
            }
            onSelectDisallowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, disallowedCountriesIds: val },
              }))
            }
          />
        </div>
      </FormSection>

      <FormSection title="SEO настройки">
        <SEOMetatagsForm
          seoMetatags={form.SEOMetatags}
          onChange={(metaTags) => setForm((prev) => ({ ...prev, SEOMetatags: metaTags }))}
        />
      </FormSection>
    </FormLayout>
  );
};

export default EditNewsPage;
